import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import config from '../config';

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background-color: white;
  color: #757575;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  max-width: 240px;
  margin: 0 auto;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);
  }
  
  img {
    margin-right: 10px;
    width: 18px;
    height: 18px;
  }
`;

const GoogleSignInButton = ({ onSuccess, onError, buttonText = 'Sign in with Google' }) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const buttonRef = useRef(null);

  // Load the Google Identity Services script
  useEffect(() => {
    console.log('GoogleSignInButton: Loading Google Identity Services script');
    
    // Check if the script is already loaded
    if (document.querySelector('script#google-identity-services')) {
      console.log('GoogleSignInButton: Script already loaded');
      setIsScriptLoaded(true);
      return;
    }
    
    const script = document.createElement('script');
    script.id = 'google-identity-services';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('GoogleSignInButton: Script loaded successfully');
      setIsScriptLoaded(true);
    };
    script.onerror = (error) => {
      console.error('GoogleSignInButton: Error loading script:', error);
      if (onError) {
        onError('Failed to load Google Identity Services');
      }
    };
    
    document.body.appendChild(script);
    
    return () => {
      // Cleanup if component unmounts
      // Note: We don't remove the script as it might be used by other components
    };
  }, [onError]);

  // Generate a state parameter to prevent CSRF attacks
  const generateState = () => {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  // Store the state in localStorage before redirecting
  const storeAuthState = (state) => {
    localStorage.setItem('auth_state', state);
    localStorage.setItem('auth_state_timestamp', Date.now().toString());
  };

  // Initiate the redirect flow for Google Sign-In
  const handleRedirectSignIn = () => {
    console.log('GoogleSignInButton: Initiating redirect flow');
    
    if (!isScriptLoaded) {
      console.error('GoogleSignInButton: Google Identity Services not loaded');
      if (onError) {
        onError('Google Identity Services not loaded');
      }
      return;
    }
    
    try {
      // Generate and store state parameter
      const state = generateState();
      storeAuthState(state);
      
      // Construct the OAuth 2.0 authorization URL
      const redirectUri = `${window.location.origin}/auth/callback`;
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(config.googleClientId)}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=code` +
        `&scope=${encodeURIComponent('openid email profile')}` +
        `&state=${encodeURIComponent(state)}` +
        `&prompt=select_account`;
      
      console.log('GoogleSignInButton: Redirecting to Google auth URL');
      window.location.href = authUrl;
    } catch (error) {
      console.error('GoogleSignInButton: Error initiating redirect flow:', error);
      if (onError) {
        onError('Failed to initiate Google Sign-In');
      }
    }
  };

  return (
    <div>
      <Button ref={buttonRef} onClick={handleRedirectSignIn}>
        <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" />
        {buttonText}
      </Button>
    </div>
  );
};

export default GoogleSignInButton;
